//
// Утилиты для работы с PWA
//

import { E_LsKeys } from '@/helpers/ls-keys'

export const PWA_UPDATE_AVAILABLE_EVENT = 'pwa-update-available'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getGlobalScope = () => globalThis as any

export const isPWAUpdateAvailableFlagSet = (): boolean => {
  if (typeof window === 'undefined') return false

  if (getGlobalScope().__ERA_PWA_UPDATE_AVAILABLE__ === true) {
    return true
  }

  try {
    return localStorage.getItem(E_LsKeys.EraPwaUpdateAvailable) === 'true'
  } catch {
    return false
  }
}

export const setPWAUpdateAvailableFlag = (): void => {
  if (typeof window === 'undefined') return

  getGlobalScope().__ERA_PWA_UPDATE_AVAILABLE__ = true

  try {
    localStorage.setItem(E_LsKeys.EraPwaUpdateAvailable, 'true')
  } catch {
    // localStorage might be blocked by privacy/browser policy
  }
}

export const clearPWAUpdateAvailableFlag = (): void => {
  if (typeof window === 'undefined') return

  getGlobalScope().__ERA_PWA_UPDATE_AVAILABLE__ = false

  try {
    localStorage.removeItem(E_LsKeys.EraPwaUpdateAvailable)
  } catch {
    // localStorage might be blocked by privacy/browser policy
  }
}

export const notifyPWAUpdateAvailable = (): void => {
  if (typeof window === 'undefined') return

  setPWAUpdateAvailableFlag()
  window.dispatchEvent(new CustomEvent(PWA_UPDATE_AVAILABLE_EVENT))
}


// Проверяет, поддерживает ли браузер Service Workers
export const isServiceWorkerSupported = (): boolean => {
  return 'serviceWorker' in navigator
}

// Проверяет, запущено ли приложение в standalone режиме
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false

  // Проверка для различных браузеров
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }

  // Проверка для iOS Safari
  if ((window.navigator as any).standalone === true) {
    return true
  }

  return false
}

// Определяет тип браузера
export const getBrowserType = (): 'chrome' | 'safari' | 'yandex' | 'firefox' | 'edge' | 'other' => {
  const userAgent = navigator.userAgent.toLowerCase()

  if (userAgent.includes('yabrowser')) {
    return 'yandex'
  } else if (userAgent.includes('edg/')) {
    return 'edge'
  } else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return 'chrome'
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    return 'safari'
  } else if (userAgent.includes('firefox')) {
    return 'firefox'
  }

  return 'other'
}

// Проверяет, поддерживает ли браузер beforeinstallprompt событие
export const supportsInstallPrompt = (): boolean => {
  const browserType = getBrowserType()
  // Chrome, Edge и некоторые другие Chromium-браузеры поддерживают
  return [ 'chrome', 'edge' ].includes(browserType)
}

// Регистрирует Service Worker
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  // В dev Service Worker часто приводит к "залипанию" старого бандла (особенно после build/preview).
  // Поэтому в development мы принудительно отключаем SW и чистим его кэш.
  if (import.meta.env.DEV) {
    if (!isServiceWorkerSupported()) return null

    try {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))

      if ('caches' in window) {
        const keys = await caches.keys()
        await Promise.all(keys.map((k) => caches.delete(k)))
      }

      console.info('[PWA] Service Worker disabled in dev (unregistered + caches cleared)')
    } catch (e) {
      console.warn('[PWA] Failed to disable Service Worker in dev', e)
    }

    return null
  }

  // Не регистрируем SW если контекст не безопасный (например, невалидный сертификат),
  // за исключением localhost (который считается безопасным)
  if (!window.isSecureContext) {
    console.warn('Service Worker не регистрируется: небезопасный контекст')
    return null
  }

  if (!isServiceWorkerSupported()) {
    console.warn('Service Worker не поддерживается в этом браузере')
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    })

    console.info('Service Worker зарегистрирован:', registration.scope)

    if (registration.waiting) {
      console.info('Service Worker update already waiting')
      notifyPWAUpdateAvailable()
    }

    // Проверка обновлений SW:
    // - в активном окне: раз в 5 минут (registration.update())
    // - в неактивном: НЕ проверяем по таймеру, только при возврате в активность
    const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000

    const safeUpdate = async () => {
      try {
        if (!navigator.onLine) return
        await registration.update()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[PWA] SW update check failed', e)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g: any = globalThis as any
    if (!g.__ERA_SW_UPDATE_WATCH__) {
      g.__ERA_SW_UPDATE_WATCH__ = true

      let intervalId: number | null = null

      const startInterval = () => {
        if (intervalId !== null) return
        intervalId = window.setInterval(() => {
          // Только когда вкладка активна/видима
          if (document.visibilityState === 'visible') {
            void safeUpdate()
          }
        }, UPDATE_CHECK_INTERVAL_MS)
      }

      const stopInterval = () => {
        if (intervalId === null) return
        clearInterval(intervalId)
        intervalId = null
      }

      const onVisibilityOrFocus = () => {
        const isActive = document.visibilityState === 'visible'
        if (isActive) {
          // При активации проверяем сразу
          void safeUpdate()
          startInterval()
        } else {
          stopInterval()
        }
      }

      // Инициализация в зависимости от текущего состояния
      onVisibilityOrFocus()

      document.addEventListener('visibilitychange', onVisibilityOrFocus)
      window.addEventListener('focus', onVisibilityOrFocus)
      window.addEventListener('blur', onVisibilityOrFocus)
      window.addEventListener('online', () => {
        // При восстановлении сети — мягко проверим обновление, если активны
        if (document.visibilityState === 'visible') {
          void safeUpdate()
        }
      })
    }

    // Обработка обновлений
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Новая версия доступна
            console.info('Новая версия приложения доступна')
            notifyPWAUpdateAvailable()
          }
        })
      }
    })

    return registration
  } catch (error) {
    console.error('Ошибка регистрации Service Worker:', error)
    return null
  }
}

// Проверяет, установлено ли PWA
export const isPWAInstalled = (): boolean => {
  // Проверка standalone режима
  if (isStandalone()) {
    return true
  }

  // Проверка localStorage
  if (localStorage.getItem('pwa-installed') === 'true') {
    return true
  }

  return false
}

// Отмечает PWA как установленное
export const markPWAAsInstalled = (): void => {
  localStorage.setItem('pwa-installed', 'true')
}

// Проверяет, был ли промпт установки отклонен
export const wasInstallPromptDismissed = (): boolean => {
  return localStorage.getItem(E_LsKeys.EraPwaPromptDismissed) === 'true'
}

// Отмечает промпт установки как отклоненный
export const markInstallPromptDismissed = (): void => {
  localStorage.setItem(E_LsKeys.EraPwaPromptDismissed, 'true')
}

// Сбрасывает состояние промпта установки
export const resetInstallPromptState = (): void => {
  localStorage.removeItem(E_LsKeys.EraPwaPromptDismissed)
}

// Получает инструкции по установке для конкретного браузера
export const getInstallInstructions = (browserType?: ReturnType<typeof getBrowserType>): string => {
  const browser = browserType || getBrowserType()

  switch (browser) {
  case 'yandex':
    return 'Для установки приложения:\n1. Нажмите на меню браузера (⋮)\n2. Выберите "Добавить на главный экран"\n3. Подтвердите установку'

  case 'safari':
    return 'Для установки приложения:\n1. Нажмите кнопку "Поделиться" (□↗)\n2. Выберите "На экран Домой"\n3. Нажмите "Добавить"'

  case 'firefox':
    return 'Для установки приложения:\n1. Нажмите на меню браузера (☰)\n2. Найдите "Установить" или "Добавить на главный экран"\n3. Подтвердите установку'

  case 'edge':
    return 'Для установки приложения:\n1. Нажмите на меню браузера (⋯)\n2. Выберите "Приложения" → "Установить это приложение"\n3. Подтвердите установку'

  case 'chrome':
    return 'Для установки приложения:\n1. Нажмите на значок установки в адресной строке\n2. Или используйте меню браузера (⋮) → "Установить приложение"\n3. Подтвердите установку'

  default:
    return 'Для установки приложения:\n1. Найдите в меню браузера опцию "Установить приложение" или "Добавить на главный экран"\n2. Подтвердите установку'
  }
}

// Показывает нативное уведомление (если разрешено)
export const showNotification = async (title: string, options?: NotificationOptions): Promise<void> => {
  if (!('Notification' in window)) {
    console.warn('Уведомления не поддерживаются')
    return
  }

  if (Notification.permission === 'granted') {
    new Notification(title, options)
  } else if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
      new Notification(title, options)
    }
  }
}

// Проверяет доступность сети
export const isOnline = (): boolean => {
  return navigator.onLine
}

// Добавляет слушатели для событий сети
export const addNetworkListeners = (onOnline?: () => void, onOffline?: () => void): (() => void) => {
  const handleOnline = () => {
    console.info('Соединение восстановлено')
    onOnline?.()
  }

  const handleOffline = () => {
    console.info('Соединение потеряно')
    onOffline?.()
  }

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // Возвращаем функцию для удаления слушателей
  return () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}
